import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import ImageSelector from 'components/ImageSelector';
import Spin from 'components/Spin';
import uploadService from 'v2/services/upload';

interface IProps {
  id: any;
  name: any;
  image?: any;
  fileName: string;
  uploadType: string;
  onChange: any;
  disabled?: boolean;
}

const CROP_SIZE = 200;
const UPLOAD_FILENAME = 'newphoto.png';

const dataURItoBlob = (dataURI: string): Blob => {
  const [header, data] = dataURI.split(',');
  const byteString = header.indexOf('base64') >= 0 ? atob(data) : unescape(data);
  const mimeString = header.split(':')[1].split(';')[0];
  const ia = new Uint8Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
};

const CropUploader = ({ id, image: imageProp, fileName, uploadType, onChange, disabled }: IProps) => {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [image, setImage] = useState<any>(imageProp);
  const [uploading, setUploading] = useState(false);

  const prevImagePropRef = useRef(imageProp);

  useEffect(() => {
    if (prevImagePropRef.current !== imageProp && !Number(imageProp)) {
      setImage(imageProp);
    }

    prevImagePropRef.current = imageProp;
  }, [imageProp]);

  const onCropComplete = useCallback(
    async (img: any) => {
      if (!img) {
        return;
      }

      setUploading(true);
      setCropperOpen(false);

      const result = await uploadService.uploadFile(dataURItoBlob(img), uploadType, UPLOAD_FILENAME);

      if (!result) {
        toast.error('Erro ao fazer upload da imagem');
        setUploading(false);
        onChange(fileName, '');
        return;
      }

      const uploadData = result?.data ?? result;
      setImage(img);
      setUploading(false);
      onChange(fileName, uploadData.upload_id, img);
    },
    [uploadType, onChange, fileName]
  );

  const onCloseCrop = useCallback(() => {
    setCropperOpen(false);
  }, []);

  const openSelector = useCallback(() => {
    if (disabled) {
      return;
    }

    setCropperOpen(true);
  }, [disabled]);

  return (
    <div className='bg-fill-secondary border-outline-default relative mt-2 flex size-full max-h-50 min-h-25 items-center justify-center overflow-hidden rounded-sm border-2 border-dashed p-6 transition-colors'>
      {!image && !uploading && (
        <button
          className='absolute inset-0 size-full cursor-pointer border-none bg-transparent'
          onClick={openSelector}
          id={id}
          disabled={disabled}
          type='button'
        >
          Escolha a imagem
        </button>
      )}
      {image && !uploading && (
        <button
          className='flex size-full cursor-pointer items-center justify-center border-none bg-transparent p-0'
          onClick={openSelector}
          id={id}
          disabled={disabled}
          type='button'
        >
          <img className='max-h-50 max-w-full' src={image} alt='cropuploader' />
        </button>
      )}
      {uploading && <Spin />}
      <ImageSelector
        width={CROP_SIZE}
        height={CROP_SIZE}
        onClose={onCloseCrop}
        onComplete={onCropComplete}
        opened={cropperOpen}
      />
    </div>
  );
};

export default CropUploader;
